import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';

import { Divider } from '@mui/material';
import { ConfirmationModal } from '../../../../modals';
import AccessConfirmation from '../../../clockings/HamburgerMenu/AccessConfirmation';
import InitCometeClockingModal from '../../../../features/cometeOnTime/InitCometeClockingModal';
import AvatarMenuItem from './AvatarMenuItem';
import CloseButton from '../../../../features/clockings/ClockingButtons/ClockingButton/CloseButton';

import useAuth from '../../../../../hooks/useAuth';
import { useDispatch, useSelector } from '../../../../../redux/store';
import { logoutClocking, setError, verifyAccess } from '../../../../../redux/slices/clockings';
import { PATH_AUTH, PATH_PAGE } from '../../../../../routes/paths';
import { clearOnTimeLoginFlags, isOnTimeUserLogin } from '../../../../../utils/sessionFlags';

const LoginSchema = Yup.object().shape({
  email: Yup.string().default('').required("L'e-mail est requis"),
  password: Yup.string().required('Mot de passe requis'),
});

/**
 * Menu OnTime pour l'espace agent/client.
 * - Les MenuItem sont rendus inline (à placer dans le popover)
 * - Les modales sont gérées via useImperativeHandle pour survivre à la fermeture du popover
 *
 * Usage :
 *   const onTimeRef = useRef();
 *   <OnTimeMenuModals ref={onTimeRef} />          ← toujours monté, hors popover
 *   <OnTimeMenuContent onTimeRef={onTimeRef} />   ← dans le popover
 */

// -- Modales (toujours montées, en dehors du popover) --
export const OnTimeMenuModals = forwardRef((_, ref) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { login, logout } = useAuth();

  const [openModal, setOpenModal] = useState(false);
  const [openModalInit, setOpenModalInit] = useState(false);
  const [isDisconnect, setIsDisconnect] = useState(false);
  const [restrictedAction, setRestrictedAction] = useState(null);

  const { getValues, control, register, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(LoginSchema),
  });

  const handleSubmit = (disconnect) => {
    const values = getValues();
    dispatch(
      verifyAccess({ email: values.email, password: values.password }, (result) => {
        if (result.success) {
          if (!disconnect) {
            login({ ...values, redirect: false });
            switch (restrictedAction) {
              case 'settings':
                navigate(PATH_AUTH.clockingApp.settings, { state: { edit: true } });
                break;
              case 'sites':
              default:
                navigate(PATH_PAGE.clockingApp.sitesManagement);
                break;
            }
          }
          reset();
          setOpenModal(false);
        } else {
          enqueueSnackbar(result.message, {
            variant: 'error',
            action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />,
          });
        }
      })
    );
  };

  useImperativeHandle(ref, () => ({
    openCode: () => setOpenModalInit(true),
    openAccess: (action, disconnect = false) => {
      setRestrictedAction(action);
      setIsDisconnect(disconnect);
      setOpenModal(true);
    },
  }));

  return (
    <>
      <ConfirmationModal
        title="Accès manager"
        confirmText="Confirmer"
        open={openModal}
        handleConfirm={() => handleSubmit(isDisconnect)}
        handleClose={() => { setOpenModal(false); reset(); }}
      >
        <AccessConfirmation
          control={control}
          errors={errors}
          isDisconnect={!!isDisconnect}
          email={LoginSchema.email}
          register={register}
          setValue={setValue}
          handleSubmit={handleSubmit}
        />
      </ConfirmationModal>

      <InitCometeClockingModal showModal={openModalInit} onClose={() => setOpenModalInit(false)} />
    </>
  );
});

// -- Items du menu (à placer dans le popover) --
export const OnTimeMenuContent = ({ onTimeRef, handleClose }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { clockingDeviceSites } = useSelector((state) => state.clockings);
  const dispatch = useDispatch();

  const handleClickCode = () => {
    handleClose();
    onTimeRef.current?.openCode();
  };

  const handleClickAccess = (action) => {
    handleClose();
    onTimeRef.current?.openAccess(action);
  };

  const handleLogout = async () => {
    handleClose();

    // Manager connecté par email OU agent connecté par code : les flags doivent être
    // retirés, sinon les guards re-redirigent vers le home utilisateur.
    const wasUserLogin = isOnTimeUserLogin();
    if (wasUserLogin) {
      clearOnTimeLoginFlags();
      // Défensif : on enlève aussi le flag d'erreur "device_not_configured" au cas où
      // une session précédente l'aurait laissé — sinon l'AuthGuard l'utiliserait pour
      // forcer le retour sur /pointages/login.
      sessionStorage.removeItem('ontime_login_error');
    }

    dispatch(logoutClocking());
    dispatch(setError(null));

    try {
      // Connexion avec le compte de la pointeuse (auto-login par uuid) : la session du
      // manager/agent/client ne doit pas rester active sur l'appareil partagé, et l'état
      // post-logout redevient identique à l'état post-redémarrage (rôle clocking_device).
      await login({ redirect: false });
    } catch (err) {
      // Auto-login impossible (réseau, appareil non appairé, is_auto_login désactivé) :
      // ApiService a restauré la session courante. On remonte l'erreur à Sentry avec un
      // code dédié pour la tracer, puis on force un rechargement complet sur la racine
      // afin de repartir d'un état kiosque propre (équivalent post-redémarrage).
      Sentry.captureException(err, {
        tags: { code: 'ontime_clocking_device_autologin_failed' },
        extra: {
          message: 'Auto-login compte pointeuse impossible après déconnexion OnTime',
          wasUserLogin,
        },
      });

      // Flag transitoire consommé par GuestGuard pour distinguer un logout d'un fresh
      // login : empêche la branche "manager + !isDevicePaired" de bouncer vers
      // /pointages/pairing si `isDevicePaired` est transitoirement false post-logout.
      if (wasUserLogin) {
        sessionStorage.setItem('ontime_just_logged_out', '1');
      }

      window.location.pathname = '/';
      return;
    }

    dispatch(setError(null));
    navigate(PATH_AUTH.clockingApp.clockingInAndOut);
  };

  return (
    <>
      <Divider />
      <AvatarMenuItem icon="logout" text="Se déconnecter" onClick={handleLogout} id="nav-logout" />
    </>
  );
};
